import { useEffect, useRef, useState } from "react";
import ErrorText from "@/components/ErrorText/ErrorText";
import FormModal from "@/components/FormModal/FormModal";
import LocationPinIcon from "@/components/SVGComponents/LocationPinIcon";
import { combineClasses } from "@/lib/utils";
import { loadGoogleMaps } from "../../lib/googleMapsLoader";
import styles from "./LocationPickerModal.module.scss";

export interface LatLng {
  lat: number;
  lng: number;
}

export interface LocationPickerResult extends LatLng {
  // "https://maps.google.com/?q=<lat>,<lng>" — opens directly in Google
  // Maps from a plain coordinate pair.
  url: string;
}

export interface LocationPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (result: LocationPickerResult) => void;
  initialValue?: LatLng | null;
  // The gathering's "City / chapter" — a pin outside this city can't be
  // confirmed, and the map opens centered on it when there's no pin yet.
  cityName?: string | undefined;
}

// No confirmed default city for a brand-new gathering's map pin — centers
// on Mumbai (this org's home city, matching CHAPTER_OPTIONS' precedent
// elsewhere) rather than an arbitrary lat/lng.
const DEFAULT_CENTER: LatLng = { lat: 19.076, lng: 72.8777 };
const DEFAULT_ZOOM = 12;

const toGoogleMapsUrl = ({ lat, lng }: LatLng) => `https://maps.google.com/?q=${lat.toFixed(6)},${lng.toFixed(6)}`;

const normalizeName = (value: string) => value.trim().toLowerCase();

// Address component types a city name can show up under in a reverse-geocode
// result — e.g. Mumbai is a `locality`, but some cities only appear as an
// `administrative_area_level_2` (district) or `postal_town`.
const CITY_COMPONENT_TYPES = ["locality", "postal_town", "administrative_area_level_2", "administrative_area_level_3"];

const getCityNamesAt = async (point: LatLng): Promise<{ names: Set<string>; displayName: string | null }> => {
  const { results } = await new google.maps.Geocoder().geocode({ location: point });
  const names = new Set<string>();
  let displayName: string | null = null;
  results.forEach((result) =>
    result.address_components.forEach((component) => {
      if (!component.types.some((type) => CITY_COMPONENT_TYPES.includes(type))) return;
      names.add(normalizeName(component.long_name));
      names.add(normalizeName(component.short_name));
      if (!displayName && component.types.includes("locality")) displayName = component.long_name;
    }),
  );
  return { names, displayName };
};

// Same brown-pin-on-white-dot look this had before the switch from
// Leaflet/OpenStreetMap to the real Google Maps JS API — a custom marker
// `icon` rather than Google's default red pin, so the visual didn't need
// to change along with the map engine underneath it.
const PIN_ICON_URL =
  "data:image/svg+xml;charset=UTF-8," +
  encodeURIComponent(
    '<svg width="30" height="42" viewBox="0 0 30 42" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M15 0C6.7 0 0 6.7 0 15c0 10.5 15 27 15 27s15-16.5 15-27C30 6.7 23.3 0 15 0z" fill="#8a5a2c"/>' +
    '<circle cx="15" cy="15" r="6" fill="#ffffff"/>' +
    "</svg>",
  );

const LocationPickerModal = ({ isOpen, onClose, onConfirm, initialValue, cityName }: LocationPickerModalProps) => {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const searchContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const searchBoxRef = useRef<google.maps.places.PlaceAutocompleteElement | null>(null);
  const [point, setPoint] = useState<LatLng | null>(initialValue ?? null);
  const [loadError, setLoadError] = useState(false);
  const [cityError, setCityError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);

  // The modal's content unmounts entirely while closed (see Modal.tsx),
  // so `mapContainerRef`'s node is fresh every time this becomes `true` —
  // same "build the map from scratch on each open" shape the previous
  // Leaflet version used, just loading the Google Maps script (once, ever
  // — see googleMapsLoader.ts) instead of importing a library.
  useEffect(() => {
    if (!isOpen) return;
    setPoint(initialValue ?? null);
    setLoadError(false);
    setCityError(null);

    let cancelled = false;

    loadGoogleMaps()
      .then(() => {
        if (cancelled || !mapContainerRef.current) return;

        const map = new google.maps.Map(mapContainerRef.current, {
          center: initialValue ?? DEFAULT_CENTER,
          zoom: DEFAULT_ZOOM,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
        });

        const dropPin = (position: google.maps.LatLng | google.maps.LatLngLiteral) => {
          if (markerRef.current) {
            markerRef.current.setPosition(position);
            return;
          }
          markerRef.current = new google.maps.Marker({
            position,
            map,
            icon: {
              url: PIN_ICON_URL,
              scaledSize: new google.maps.Size(30, 42),
              anchor: new google.maps.Point(15, 42),
            },
          });
        };

        if (initialValue) {
          dropPin(initialValue);
        } else if (cityName) {
          new google.maps.Geocoder()
            .geocode({ address: cityName })
            .then(({ results }) => {
              const viewport = results[0]?.geometry.viewport;
              if (!cancelled && viewport) map.fitBounds(viewport);
            })
            .catch(() => {
              // Keep DEFAULT_CENTER — centering is only a convenience.
            });
        }

        // Filled in once the search box below exists — keeps its text (and
        // the × button) in sync when the pin moves by a map click.
        let setSearchText: (text: string) => void = () => {};
        // Only the latest click's reverse-geocode may write the search text,
        // so a slow earlier lookup can't overwrite a newer one.
        let clickId = 0;

        map.addListener("click", (event: google.maps.MapMouseEvent) => {
          if (!event.latLng) return;
          const clicked = { lat: event.latLng.lat(), lng: event.latLng.lng() };
          setCityError(null);
          setPoint(clicked);
          dropPin(event.latLng);

          const currentClickId = ++clickId;
          new google.maps.Geocoder()
            .geocode({ location: clicked })
            .then(({ results }) => {
              if (cancelled || currentClickId !== clickId) return;
              setSearchText(results[0]?.formatted_address ?? "");
            })
            .catch(() => {
              if (!cancelled && currentClickId === clickId) setSearchText("");
            });
        });

        mapRef.current = map;

        // The modern replacement for the (deprecated, closed to new API
        // keys since March 2025) `places.Autocomplete` widget — a custom
        // element that renders its own search input + suggestion dropdown,
        // so it's appended into its own container rather than bound to a
        // plain <input> the old widget used.
        if (searchContainerRef.current) {
          const searchBox = new google.maps.places.PlaceAutocompleteElement({
            noClearButton: true,
          });

          searchBox.placeholder = "Search for a place or address";

          searchContainerRef.current.appendChild(searchBox);
          searchBoxRef.current = searchBox;

          // Custom clear button
          const clearButton = document.createElement("button");

          clearButton.type = "button";
          clearButton.className = styles.clearButton ?? "";
          clearButton.setAttribute("aria-label", "Clear search");
          clearButton.textContent = "×";

          // Don't show × when input is empty
          clearButton.style.display = "none";

          searchBox.addEventListener("input", () => {
            clearButton.style.display = searchBox.value.trim() ? "flex" : "none";
          });

          clearButton.addEventListener("click", () => {
            searchBox.value = "";
            clearButton.style.display = "none";
            searchBox.focus();
          });

          searchContainerRef.current.appendChild(clearButton);

          setSearchText = (text) => {
            searchBox.value = text;
            clearButton.style.display = text.trim() ? "flex" : "none";
          };

          searchBox.addEventListener("gmp-select", (event) => {
            void (async () => {
              const { place } = await event.placePrediction
                .toPlace()
                .fetchFields({ fields: ["location"] });

              const location = place.location;

              if (!location) return;

              setCityError(null);
              setPoint({
                lat: location.lat(),
                lng: location.lng(),
              });

              dropPin(location);
              map.panTo(location);
              map.setZoom(15);
            })();
          });

          searchBox.addEventListener("gmp-error", (event) => {
            console.error("Google Places Autocomplete error:", event);
          });
        }
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });

    return () => {
      cancelled = true;
      mapRef.current = null;
      markerRef.current = null;
      searchBoxRef.current?.remove();
      searchBoxRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClose = () => {
    setPoint(initialValue ?? null);
    onClose();
  };

  const handleConfirm = async () => {
    if (!point) return;
    if (cityName) {
      setIsVerifying(true);
      try {
        const { names, displayName } = await getCityNamesAt(point);
        if (!names.has(normalizeName(cityName))) {
          setCityError(
            displayName
              ? `This location is in ${displayName}, not ${cityName}. Pick a location within ${cityName}.`
              : `This location isn't in ${cityName}. Pick a location within ${cityName}.`,
          );
          return;
        }
      } catch {
        setCityError(`Couldn't verify that this location is in ${cityName}. Try again.`);
        return;
      } finally {
        setIsVerifying(false);
      }
    }
    onConfirm({ ...point, url: toGoogleMapsUrl(point) });
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Pick a Location"
      subtitle="Search for a place, or click anywhere on the map to drop a pin."
      onSubmit={(event) => {
        event.preventDefault();
        void handleConfirm();
      }}
      submitLabel={isVerifying ? "Checking location…" : "Use this location"}
      submitIcon={<LocationPinIcon width={16} height={16} />}
      submitDisabled={!point || isVerifying}
      className={combineClasses(styles.locationPickerModal)}
    >
      {loadError ? (
        <div className={combineClasses(styles.mapContainer, styles.mapError)}>
          Couldn&apos;t load the map. Check your connection and try again.
        </div>
      ) : (
        <>
          <div ref={searchContainerRef} className={styles.searchContainer} />
          <div ref={mapContainerRef} className={styles.mapContainer} />
        </>
      )}
      <p className={styles.pointPreview}>{point ? toGoogleMapsUrl(point) : "No location selected yet"}</p>
      {cityError && <ErrorText>{cityError}</ErrorText>}
    </FormModal>
  );
};

export default LocationPickerModal;
